import React, { useEffect, useState, forwardRef, useImperativeHandle,useRef } from 'react';
import './CustomSelectFilter.css';

/**
 * Custom Multi-Select Filter for AG Grid Server-Side Model
 *
 * Usage in column definition:
 * {
 *   headerName: "Status",
 *   field: "status_name",
 *   filter: CustomSelectFilter,
 *   filterParams: {
 *     options: ["Open", "Closed", "In Progress"],
 *   },
 * }
 */
const CustomSelectFilter = forwardRef((props, ref) => {
  const [appliedValues, setAppliedValues] = useState([]); // Actually applied filter
  const [selectedValues, setSelectedValues] = useState([]); // Pending selection
  const [searchText, setSearchText] = useState('');
  const filterChangedCallbackRef = useRef(props.filterChangedCallback);
  const previousAppliedValuesRef = useRef([]);

  const colDef = props.colDef;

  // Keep callback ref updated
  useEffect(() => {
    filterChangedCallbackRef.current = props.filterChangedCallback;
  }, [props.filterChangedCallback]);

  // Check multiple possible locations for filterParams
  const filterParams = props.filterParams || props.colDef?.filterParams || {};
  const options = filterParams.options || [];

  console.log('🔍 CustomSelectFilter:', {
    field: colDef?.field,
    optionsCount: options.length,
    options: options,
  });

  useImperativeHandle(ref, () => {
    return {
      // AG Grid calls this to check if filter is active
      isFilterActive() {
        return appliedValues.length > 0;
      },

      // AG Grid calls this to check if row passes filter
      doesFilterPass(params) {
        if (appliedValues.length === 0) return true;
        const value = props.valueGetter(params.node);
        return appliedValues.includes(value);
      },

      // AG Grid calls this to get current filter state
      getModel() {
        if (appliedValues.length === 0) return null;

        // Always use inArray with JSON stringified array (matching tableFunctions.js)
        return {
          filterType: 'text',
          type: 'inArray',
          filter: JSON.stringify(appliedValues),
        };
      },

      // AG Grid calls this to set filter state (e.g., when loading saved filters)
      setModel(model) {
        if (model == null) {
          setAppliedValues([]);
          setSelectedValues([]);
        } else if (model.filter) {
          let values;

          // Handle inArray type - filter is always an array
          if (Array.isArray(model.filter)) {
            values = model.filter;
          } else if (typeof model.filter === 'string') {
            // Try parsing JSON string first
            try {
              const parsed = JSON.parse(model.filter);
              values = Array.isArray(parsed) ? parsed : [parsed];
            } catch (e) {
              // Not JSON, treat as single value
              values = [model.filter];
            }
          } else {
            // Single value
            values = [model.filter];
          }

          setAppliedValues(values);
          setSelectedValues(values);
        }
      },

      // Required for sidebar filters panel to display filter names and summary
      getModelAsString(model) {
        if (!model || !model.filter) return '';

        try {
          const values = typeof model.filter === 'string'
            ? JSON.parse(model.filter)
            : model.filter;

          if (Array.isArray(values) && values.length > 0) {
            return values.length === 1
              ? values[0]
              : `${values.length} selected`;
          }
        } catch (e) {
          return '';
        }

        return '';
      },

      // Lifecycle method called when filter GUI is removed/destroyed
      afterGuiAttached(params) {
        // Optional: can be used to focus search input when filter opens
      },

      // Lifecycle method called when filter GUI is about to be destroyed
      afterGuiDetached() {
        // Cleanup if needed
      },
    };
  });

  // Only call filterChangedCallback when appliedValues actually changes
  // This prevents triggering API calls when just opening/closing the filter
  useEffect(() => {
    const previousValues = previousAppliedValuesRef.current;

    // Check if values actually changed (deep comparison)
    const valuesChanged =
      appliedValues.length !== previousValues.length ||
      appliedValues.some((val, index) => val !== previousValues[index]);

    // Update the ref to current values for next comparison
    previousAppliedValuesRef.current = appliedValues;

    // Only trigger API call if values actually changed
    if (!valuesChanged) {
      return;
    }

    // Values changed - trigger API call
    filterChangedCallbackRef.current();
  }, [appliedValues, colDef?.field]);

  const handleCheckboxChange = (option) => {
    setSelectedValues((prev) => {
      if (prev.includes(option)) {
        return prev.filter((v) => v !== option);
      } else {
        return [...prev, option];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedValues.length === filteredOptions.length) {
      setSelectedValues([]);
    } else {
      setSelectedValues([...filteredOptions]);
    }
  };

  const handleClearAll = () => {
    setSelectedValues([]);
    setSearchText('');
  };

  const handleApply = () => {
    setAppliedValues([...selectedValues]);
    props.api.hidePopupMenu();
  };

  const handleReset = () => {
    setSelectedValues([...appliedValues]);
    setSearchText('');
  };

  // Filter options based on search text
  const filteredOptions = options.filter((option) =>
    option.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <div className="custom-select-filter">
      {/* Search input */}
      <div className="filter-search">
        <input
          type="text"
          placeholder="Search..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className="filter-search-input"
        />
      </div>

      {/* Select All / Clear All */}
      <div className="filter-actions">
        <label className="filter-checkbox-item">
          <input
            type="checkbox"
            checked={selectedValues.length === filteredOptions.length && filteredOptions.length > 0}
            onChange={handleSelectAll}
          />
          <span className="filter-checkbox-label">
            (Select All)
          </span>
        </label>
      </div>

      {/* Options list */}
      <div className="filter-options-list">
        {filteredOptions.length === 0 ? (
          <div className="filter-no-results">No options found</div>
        ) : (
          filteredOptions.map((option, index) => (
            <label key={index} className="filter-checkbox-item">
              <input
                type="checkbox"
                checked={selectedValues.includes(option)}
                onChange={() => handleCheckboxChange(option)}
              />
              <span className="filter-checkbox-label">{option}</span>
            </label>
          ))
        )}
      </div>

      {/* Footer with Apply and Reset buttons */}
      <div className="filter-footer-buttons">
        <button onClick={handleReset} className="filter-reset-btn">
          Reset
        </button>
        <button onClick={handleApply} className="filter-apply-btn">
          Apply
        </button>
      </div>
    </div>
  );
});

CustomSelectFilter.displayName = 'CustomSelectFilter';

export default CustomSelectFilter;
